import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';

type OverlayProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  safeAreaPadding?: boolean;
  constrainPanelWidth?: boolean;
};

/**
 * Knox Overlay — replaces folds Overlay + OverlayBackdrop + OverlayCenter + focus-trap-react.
 *
 * Built on @headlessui/react Dialog which provides:
 * - Focus trapping (no focus-trap-react dependency)
 * - Escape key to close
 * - Click-outside to close (via backdrop)
 * - Portal rendering
 * - Proper aria attributes
 */
export function Overlay({
  open,
  onClose,
  children,
  className,
  safeAreaPadding = false,
  constrainPanelWidth = false,
}: OverlayProps) {
  return (
    <Dialog open={open} onClose={onClose} className={clsx('relative z-50', className)}>
      {/* Semi-transparent backdrop with fade transition */}
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/60 transition-opacity duration-200 data-[closed]:opacity-0"
      />

      {/* Centered panel wrapper */}
      <div
        className={clsx(
          'fixed inset-0 flex items-center justify-center p-4',
          safeAreaPadding && [
            'max-[750.99px]:pt-[max(16px,env(safe-area-inset-top))]',
            'max-[750.99px]:pr-[max(16px,env(safe-area-inset-right))]',
            'max-[750.99px]:pb-[max(16px,env(safe-area-inset-bottom))]',
            'max-[750.99px]:pl-[max(16px,env(safe-area-inset-left))]',
          ],
        )}
      >
        <DialogPanel
          transition
          className={clsx(
            'transition-all duration-200 data-[closed]:opacity-0 data-[closed]:scale-95',
            constrainPanelWidth && 'max-w-full',
          )}
        >
          {children}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
